import { z } from 'zod';

// ─── member diagnoses vocabulary ─────────────────────────────────────────────
//
// Governed enums for `member_diagnoses`: the one consolidated history of a
// member's substance-use-disorder diagnoses. Pelago 1.0 kept these in four
// shape-incompatible tables (`aud_diagnosis`, `oud_diagnosis`,
// `cud_diagnosis`, `stud_diagnosis`); Galapagos keeps one row per diagnosis
// with a `kind` discriminator, one severity scale and the eleven DSM-5
// criteria as typed columns. ICD-10 coded diagnoses are a different fact and
// are NOT a kind here. The table is append-only: the current diagnosis of a
// kind is the latest `recorded_at`.
//
// Dependency-free on purpose: `db/schema.ts` renders these arrays into its
// CHECK constraints and imports from here (never the reverse).

/** One row per diagnosed substance; a 1.0 STUD row yields two (amphetamine, cocaine). */
export const MEMBER_DIAGNOSIS_KIND = [
  'aud',
  'oud',
  'cud',
  'stud_amphetamine',
  'stud_cocaine',
] as const;
export const memberDiagnosisKindSchema = z.enum(MEMBER_DIAGNOSIS_KIND);
export type MemberDiagnosisKind = z.infer<typeof memberDiagnosisKindSchema>;

/** The kinds that carry DSM-5 criteria; every other kind has all eleven NULL. */
export const MEMBER_DIAGNOSIS_CRITERIA_KINDS = [
  'aud',
  'oud',
] as const satisfies readonly MemberDiagnosisKind[];

/** One severity scale for every kind. NULL on a row means 1.0 stored none. */
export const MEMBER_DIAGNOSIS_SEVERITY = ['none', 'mild', 'moderate', 'severe'] as const;
export const memberDiagnosisSeveritySchema = z.enum(MEMBER_DIAGNOSIS_SEVERITY);
export type MemberDiagnosisSeverity = z.infer<typeof memberDiagnosisSeveritySchema>;

/**
 * Each diagnosis kind's latest recorded severity for one member. A kind with
 * no row, or only rows whose severity was never recorded, is absent.
 */
export type LatestDiagnosisByKind = Partial<Record<MemberDiagnosisKind, MemberDiagnosisSeverity>>;

/** Who recorded an AUD diagnosis in 1.0; `user` is a member self-assessment. */
export const MEMBER_DIAGNOSIS_CREATOR_TYPE = ['physician', 'quitCoach', 'user'] as const;
export const memberDiagnosisCreatorTypeSchema = z.enum(MEMBER_DIAGNOSIS_CREATOR_TYPE);
export type MemberDiagnosisCreatorType = z.infer<typeof memberDiagnosisCreatorTypeSchema>;

/**
 * Where a row came from. Only the 1.0 import writes today; a Galapagos-native
 * writer adds its value here (and to the CHECK) when it lands.
 */
export const MEMBER_DIAGNOSIS_SOURCE = ['legacy_import', 'healthie_form'] as const;
export const memberDiagnosisSourceSchema = z.enum(MEMBER_DIAGNOSIS_SOURCE);
export type MemberDiagnosisSource = z.infer<typeof memberDiagnosisSourceSchema>;

/** The 1.0 table an imported row was read from. */
export const MEMBER_DIAGNOSIS_SOURCE_TABLE = [
  'aud_diagnosis',
  'oud_diagnosis',
  'cud_diagnosis',
  'stud_diagnosis',
  'healthie_form_answer_group',
] as const;
export const memberDiagnosisSourceTableSchema = z.enum(MEMBER_DIAGNOSIS_SOURCE_TABLE);
export type MemberDiagnosisSourceTable = z.infer<typeof memberDiagnosisSourceTableSchema>;

/**
 * The eleven DSM-5 substance-use-disorder criteria, in DSM order. The column
 * for each is `criterion_<key>`; 1.0's AUD `*Response` columns and OUD
 * shorthand columns both map onto this one set.
 */
export const MEMBER_DIAGNOSIS_CRITERIA = [
  'larger_or_longer',
  'cutdown_unsuccessful',
  'time_spent',
  'craving',
  'obligation_failure',
  'interpersonal_problems',
  'giving_up_activities',
  'physically_hazardous',
  'physical_or_psychological_problem',
  'tolerance',
  'withdrawal',
] as const;
export type MemberDiagnosisCriterion = (typeof MEMBER_DIAGNOSIS_CRITERIA)[number];

/** The raw 1.0 severity label, kept beside the normalised severity. */
export const MEMBER_DIAGNOSIS_LEGACY_LABEL_MAX_LENGTH = 128;
/** 1.0's `creatorName varchar(255)`: a staff name, never emitted to telemetry. */
export const MEMBER_DIAGNOSIS_CREATOR_NAME_MAX_LENGTH = 255;
/** `<table>:<id>` (STUD: `stud_diagnosis:<id>:<substance>`). */
export const MEMBER_DIAGNOSIS_SOURCE_REF_MAX_LENGTH = 128;

/** Prefix for `source_ref` rows written from a locked Healthie form answer group. */
export const MEMBER_DIAGNOSIS_HEALTHIE_SOURCE_REF_PREFIX = 'healthie_form_answer_group';

const HEALTHIE_SOURCE_REF_LOCK_VERSION_LENGTH = '2026-10-27T10:00:00.000Z'.length;
const HEALTHIE_SOURCE_REF_LONGEST_KIND_LENGTH = Math.max(
  ...MEMBER_DIAGNOSIS_KIND.map((kind) => kind.length),
);

/** Max Healthie form answer group id length that keeps every `source_ref` within the column bound. */
export const HEALTHIE_DIAGNOSIS_GROUP_ID_MAX_LENGTH =
  MEMBER_DIAGNOSIS_SOURCE_REF_MAX_LENGTH -
  MEMBER_DIAGNOSIS_HEALTHIE_SOURCE_REF_PREFIX.length -
  3 -
  HEALTHIE_SOURCE_REF_LONGEST_KIND_LENGTH -
  HEALTHIE_SOURCE_REF_LOCK_VERSION_LENGTH;

/** Max ISO lock instant length stored in `healthie_diagnosis_processed_locks.lock_version`. */
export const HEALTHIE_DIAGNOSIS_LOCK_VERSION_MAX_LENGTH = 32;

/** Colons would make `source_ref` segments ambiguous against shorter group ids. */
export function healthieDiagnosisGroupIdInvalid(groupId: string): boolean {
  return groupId.includes(':');
}

/** Reads the Healthie form answer group id embedded in a `healthie_form` source ref. */
export function healthieDiagnosisGroupIdFromSourceRef(
  sourceRef: string,
  kind: MemberDiagnosisKind,
): string | null {
  const prefix = `${MEMBER_DIAGNOSIS_HEALTHIE_SOURCE_REF_PREFIX}:`;
  if (!sourceRef.startsWith(prefix)) return null;
  const tail = sourceRef.slice(prefix.length);
  const kindDelimiter = `:${kind}:`;
  const delimiterIndex = tail.lastIndexOf(kindDelimiter);
  if (delimiterIndex < 0) return null;
  return tail.slice(0, delimiterIndex);
}

export function healthieDiagnosisSourceRef(
  groupId: string,
  kind: MemberDiagnosisKind,
  lockVersion: string,
): string {
  if (groupId.length > HEALTHIE_DIAGNOSIS_GROUP_ID_MAX_LENGTH) {
    throw new Error('healthie_diagnosis_source_ref_group_id_too_long');
  }
  if (healthieDiagnosisGroupIdInvalid(groupId)) {
    throw new Error('healthie_diagnosis_source_ref_group_id_invalid');
  }
  const ref = `${MEMBER_DIAGNOSIS_HEALTHIE_SOURCE_REF_PREFIX}:${groupId}:${kind}:${lockVersion}`;
  if (ref.length > MEMBER_DIAGNOSIS_SOURCE_REF_MAX_LENGTH) {
    throw new Error('healthie_diagnosis_source_ref_too_long');
  }
  return ref;
}

export type SeverityLabelWarning = 'empty_severity_label' | 'unrecognised_severity_label';

export type SeverityFromLabelContext = {
  warn?: (code: SeverityLabelWarning) => void;
};

// A Map, not an object literal: a label starting with `constructor` or
// `toString` must miss, not find an inherited property.
const LEADING_WORD: ReadonlyMap<string, MemberDiagnosisSeverity> = new Map([
  ['No', 'none'],
  ['Mild', 'mild'],
  ['Moderate', 'moderate'],
  ['Severe', 'severe'],
]);

/**
 * The normalised severity of a 1.0 or Healthie option label. NULL stays NULL
 * silently; an empty label or one outside the four families is NULL with a
 * named warning — never a guess.
 */
export function severityFromLabel(
  label: string | null,
  ctx: SeverityFromLabelContext,
): MemberDiagnosisSeverity | null {
  if (label === null) return null;
  if (label.trim() === '') {
    ctx.warn?.('empty_severity_label');
    return null;
  }
  const severity = LEADING_WORD.get(label.trim().split(/\s+/)[0] ?? '');
  if (severity === undefined) {
    ctx.warn?.('unrecognised_severity_label');
    return null;
  }
  return severity;
}

/**
 * The program each diagnosis kind belongs to, so a staff surface places a
 * diagnosis on its program without holding substance logic of its own. Total
 * over the kinds by `satisfies`: a new kind without a program fails `tsc`. Both
 * STUD kinds belong to the one stimulants program, as 1.0's one STUD row did.
 */
export const MEMBER_DIAGNOSIS_KIND_TO_PROGRAM_KEY = {
  aud: 'alcohol',
  oud: 'opioid',
  cud: 'cannabis',
  stud_amphetamine: 'stimulants',
  stud_cocaine: 'stimulants',
} as const satisfies Readonly<Record<MemberDiagnosisKind, string>>;

/** The programs that carry a diagnosis, each once. */
export const MEMBER_DIAGNOSIS_PROGRAM_KEYS: readonly string[] = Object.freeze([
  ...new Set(Object.values(MEMBER_DIAGNOSIS_KIND_TO_PROGRAM_KEY)),
]);
